/**
 * Imperative replacements for window.alert / window.confirm that render through the shared Modal
 * (see DialogHost). Usage:
 *   await notify("User created successfully");
 *   if (!(await confirmDialog("Delete this user?"))) return;
 */
export type DialogRequest =
  | { kind: "alert"; message: string; title?: string; tone: "success" | "error"; resolve: () => void }
  | { kind: "confirm"; message: string; title?: string; confirmLabel: string; destructive: boolean; resolve: (ok: boolean) => void };

type Listener = (req: DialogRequest) => void;
let listener: Listener | null = null;

export const registerDialogHost = (fn: Listener | null) => {
  listener = fn;
};

const ERROR_HINT = /error|fail|cannot|can't|unable|invalid|denied|not allowed/i;
const DESTRUCTIVE_HINT = /delete|remove|permanently|cannot be undone/i;

export function notify(message: string, options: { title?: string; tone?: "success" | "error" } = {}): Promise<void> {
  return new Promise((resolve) => {
    if (!listener) {
      console.warn("[dialogs] no DialogHost mounted:", message);
      resolve();
      return;
    }
    listener({ kind: "alert", message, title: options.title, tone: options.tone ?? (ERROR_HINT.test(message) ? "error" : "success"), resolve });
  });
}

export function confirmDialog(
  message: string,
  options: { title?: string; confirmLabel?: string; destructive?: boolean } = {}
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!listener) {
      console.warn("[dialogs] no DialogHost mounted, refusing:", message);
      resolve(false); // never proceed with a destructive action nobody was asked about
      return;
    }
    const destructive = options.destructive ?? DESTRUCTIVE_HINT.test(message);
    listener({ kind: "confirm", message, title: options.title, destructive, confirmLabel: options.confirmLabel ?? (destructive ? "Delete" : "Confirm"), resolve });
  });
}
